import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bot, FileText, Stethoscope, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

export default function AIStudio() {
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: async () => (await api.get("/patients")).data });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
          <Bot className="h-7 w-7 text-primary" /> AI Studio
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Draft SOAP notes, referral letters and sick notes with GPT-5.4 — powered by the Emergent Universal Key.</p>
      </div>

      <Tabs defaultValue="soap">
        <TabsList data-testid="ai-tabs">
          <TabsTrigger value="soap" data-testid="ai-tab-soap">SOAP from dictation</TabsTrigger>
          <TabsTrigger value="referral" data-testid="ai-tab-referral">Referral letter</TabsTrigger>
          <TabsTrigger value="sicknote" data-testid="ai-tab-sicknote">Sick note</TabsTrigger>
        </TabsList>

        <TabsContent value="soap" className="mt-6"><SoapTool /></TabsContent>
        <TabsContent value="referral" className="mt-6"><ReferralTool patients={patients} /></TabsContent>
        <TabsContent value="sicknote" className="mt-6"><SickNoteTool patients={patients} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Output({ text, testid }) {
  if (!text) return null;
  return (
    <Card className="mt-4 rounded-2xl border-border/60 bg-secondary/40 p-5">
      <div className="mb-2 flex items-center justify-between">
        <span className="label-eyebrow">AI output</span>
        <Button size="sm" variant="ghost" className="gap-1 rounded-full" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}>
          <Copy className="h-3.5 w-3.5" /> Copy
        </Button>
      </div>
      <pre data-testid={testid} className="whitespace-pre-wrap font-sans text-sm">{text}</pre>
    </Card>
  );
}

function SoapTool() {
  const [transcript, setTranscript] = useState("");
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!transcript.trim()) return toast.error("Add dictation first");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/soap-draft", { transcript });
      setOut(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <Card className="rounded-2xl border-border/60 p-5">
      <Label className="flex items-center gap-1"><Stethoscope className="h-4 w-4 text-primary" /> Dictation</Label>
      <Textarea data-testid="studio-transcript" className="mt-2 min-h-[120px]" placeholder="Talk like you would to a colleague…" value={transcript} onChange={(e) => setTranscript(e.target.value)} />
      <Button data-testid="studio-soap-btn" onClick={run} disabled={loading} className="mt-3 gap-2 rounded-full"><Sparkles className="h-4 w-4" /> {loading ? "Drafting…" : "Draft SOAP"}</Button>
      {out && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="studio-soap-output">
          {["subjective", "objective", "assessment", "plan"].map((k) => (
            <div key={k} className="rounded-xl bg-secondary/40 p-3">
              <div className="label-eyebrow capitalize">{k}</div>
              <p className="mt-1 text-sm">{out[k]}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ReferralTool({ patients }) {
  const [pid, setPid] = useState("");
  const [specialist, setSpecialist] = useState("");
  const [reason, setReason] = useState("");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!pid || !specialist) return toast.error("Pick a patient and specialist");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/referral-letter", { patient_id: pid, specialist, reason });
      setOut(data.letter);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <Card className="rounded-2xl border-border/60 p-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label>Patient</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="referral-patient"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-popover">{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Specialist</Label><Input data-testid="referral-specialist" value={specialist} onChange={(e) => setSpecialist(e.target.value)} placeholder="Oral surgeon" /></div>
        <div><Label>Reason</Label><Input data-testid="referral-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Impacted wisdom tooth" /></div>
      </div>
      <Button data-testid="referral-btn" onClick={run} disabled={loading} className="mt-3 gap-2 rounded-full"><FileText className="h-4 w-4" /> {loading ? "Drafting…" : "Draft referral"}</Button>
      <Output text={out} testid="referral-output" />
    </Card>
  );
}

function SickNoteTool({ patients }) {
  const [pid, setPid] = useState("");
  const [days, setDays] = useState(2);
  const [diagnosis, setDiagnosis] = useState("");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!pid) return toast.error("Pick a patient");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/sick-note", { patient_id: pid, days: Number(days), diagnosis });
      setOut(data.note);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <Card className="rounded-2xl border-border/60 p-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label>Patient</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="sicknote-patient"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-popover">{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Days off</Label><Input type="number" data-testid="sicknote-days" value={days} onChange={(e) => setDays(e.target.value)} /></div>
        <div><Label>Diagnosis</Label><Input data-testid="sicknote-diagnosis" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="Post-extraction recovery" /></div>
      </div>
      <Button data-testid="sicknote-btn" onClick={run} disabled={loading} className="mt-3 gap-2 rounded-full"><FileText className="h-4 w-4" /> {loading ? "Drafting…" : "Generate sick note"}</Button>
      <Output text={out} testid="sicknote-output" />
    </Card>
  );
}
